import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  InspectionDataDto,
  InspectionExtendedAttributesDto,
  InspectionOptionRequestDto,
  InspectionReportDto,
  InspectionVehicleDto,
  InventoryService,
  LaunchDarklyService,
  OrganizationState,
  PostingDataResponseModel,
  PostingInformation,
  PostingInventoryItem,
  VehicleInformation,
  VehicleOptions,
  VinDecodeResponseModel,
  ViwInventoryRecord,
  ChromeServiceInputModel,
  ConfigurationState,
  CancelEditPosting,
  EditPostingState,
  SetChromeDataInput,
  SetInitialPosting,
  SetSelectedInspectionOption,
  ToggleSpinner,
  NewPostingInspectionOption,
  PostingFormTitle,
  PostingTabs,
  EditPostingService,
  DealerGroupMemberDto, SetDeepLinkVin, SetDeepLinkVendor
} from 'remarketing-angular-library';
import {NewPostingModalComponent} from '../new-posting-modal/new-posting-modal.component';
import {Select, Store} from '@ngxs/store';
import {forkJoin, Observable} from 'rxjs';
import {filter, mergeMap, take, takeUntil} from 'rxjs/operators';
import {InputElementMetaData} from 'remarketing-ui-components-lib';
import {SaComponent} from 'smartauction-frontend-micro-library';
import {TranslocoService} from '@jsverse/transloco';
import {cloneDeep} from 'lodash';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {TabsetComponent, TabsModule} from 'ngx-bootstrap/tabs';
import {
  PostingDataHelperService,
  PostingErrorResponse
} from '../../../../../vehicles/services/posting-data-helper/posting-data-helper.service';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { LeadSellerGroupService } from '../../../manage-inventory/services/lead-seller-group/lead-seller-group.service';
import { DealerGroupMember } from '../../../manage-inventory/interfaces/dealer-group.interface';
import { PostingSkeletonComponent } from '../posting-skeleton/posting-skeleton.component';
import {
  ThirdPartyPostingSearchResultsComponent
} from '../third-party-posting-search-results/third-party-posting-search-results.component';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-new-posting',
    templateUrl: './new-posting.component.html',
    styleUrls: ['./new-posting.component.scss'],
    imports: [
        TabsModule,
        NewPostingModalComponent,
        ReactiveFormsModule,
        PostingSkeletonComponent,
        ThirdPartyPostingSearchResultsComponent,
        CommonModule
    ]
})
export class NewPostingComponent extends SaComponent implements OnInit, AfterViewInit {
  @Select(EditPostingState.showSpinner) showSpinner$: Observable<boolean>;
  @ViewChild(NewPostingModalComponent) newPostingModal: NewPostingModalComponent;
  @ViewChild('vinDecodeInput') vinFormInputElement: ElementRef<HTMLInputElement>;
  @ViewChild('vinSubmitButton') vinSubmitButtonElement: ElementRef<HTMLElement>;
  @ViewChild('newPostDealershipSelect') newPostDealershipSelect: ElementRef<HTMLElement>;
  @ViewChild('thirdPartyPostDealershipSelect') thirdPartyPostDealershipSelect: ElementRef<HTMLElement>;
  @ViewChild('postingTabset', { static: false }) postingTabset?: TabsetComponent;

  private readonly DEFAULT = 'default';
  private readonly LEAD_SELLER_DEALERSHIP_SELECT = 'leadSellerDealershipSelection';
  showSpinner = false;
  vinPostingForm: UntypedFormGroup;
  vinDecodeResponse: VinDecodeResponseModel;
  vinDecodeVehicleInformation: VehicleInformation;
  targetVin: string;
  errorHeaderKey: string;
  errorMessageKey: string;
  existingInspectionReport: InspectionReportDto;
  chromeOptions: VehicleOptions;
  inspectionFetched = false;
  translatedInspectionActions: InputElementMetaData[];
  newPostingSeedData: PostingDataResponseModel;
  sellerOeUuid: string;
  postingTabs = PostingTabs;
  activeTab: PostingTabs;
  thirdPartySearchResults: ViwInventoryRecord[];
  newPostTranslations = {
    textNewPostTabHeader: '',
    textPostingHeader: '',
    textThirdPartyPostTabHeader: '',
    textVinDecodePlaceholder: '',
    textEnterValidVinError: '',
    textPostButton: '',
    dealershipsLabel: '',
    noDealershipsFoundMessage: '',
    select: '',
    selectDealer: ''
  };
  thirdPartyPostTranslations = {
    textThirdPartyPostVinPlaceHolder: '',
    textThirdPartyPostSearchButton: '',
    textEnterValidVinError: ''
  };
  textResults = '';
  chromeDataInput: ChromeServiceInputModel = {
    manufacturerModelCode: undefined,
    vehicleTrimName: undefined,
    manufacturerOptionCodes: undefined,
    equipmentDescriptions: undefined,
    exteriorColor: undefined,
    interiorColor: undefined
  };
  postingErrorResponse: PostingErrorResponse = {
    errorHeaderKey: undefined,
    errorMessageKey: undefined
  };

  thirdPartyPostingForm: UntypedFormGroup;
  thirdPartyTargetVin: string;
  dealershipOptions: DealerGroupMember[] = undefined;
  selectedDealership: DealerGroupMember;
  isDeepLink = false;

  /**
   * Common attributes for radio buttons.
   */
  DEFAULT_RADIO_BUTTON = {
    class: 'custom-control-input radio',
    groupName: 'inspection-actions'
  } as Partial<InputElementMetaData>;

  /**
   * The default list of actions a user can take when an inspection is returned mapped to radio buttons.
   */
  DEFAULT_INSPECTION_ACTIONS = [
    {
      ...this.DEFAULT_RADIO_BUTTON,
      name: NewPostingInspectionOption.USE_EXISTING,
      ariaLabel: 'Use this inspection',
      dataTrackname: 'Existing Inspection New Posting',
      id: NewPostingInspectionOption.USE_EXISTING,
      isChecked: true,
      value: 'Use this inspection'
    },
    {
      ...this.DEFAULT_RADIO_BUTTON,
      name: NewPostingInspectionOption.CREATE_NEW,
      ariaLabel: 'Create a new inspection',
      dataTrackname: 'New Inspection New Posting',
      id: NewPostingInspectionOption.CREATE_NEW,
      isChecked: false,
      value: 'Create a new inspection'
    },
    {
      ...this.DEFAULT_RADIO_BUTTON,
      name: NewPostingInspectionOption.WAIT_FOR_INSPECTION,
      ariaLabel: 'Wait for another inspection',
      dataTrackname: 'Wait for Inspection New Posting',
      id: NewPostingInspectionOption.WAIT_FOR_INSPECTION,
      isChecked: false,
      value: 'Wait for another inspection'
    }
  ] as InputElementMetaData[];

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly inventoryService: InventoryService,
    private readonly store: Store,
    private readonly elRef: ElementRef,
    private readonly translocoService: TranslocoService,
    private readonly router: Router,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly editPostingService: EditPostingService,
    private readonly activatedRoute: ActivatedRoute,
    public readonly postingDataHelperService: PostingDataHelperService,
    public readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    public readonly leadSellerGroupService: LeadSellerGroupService
  ) {
    super();
  }

  ngOnInit(): void {
    this.store.dispatch(new CancelEditPosting());
    this.sellerOeUuid = this.store.selectSnapshot(OrganizationState.oeUuid);
    this.vinPostingForm = this.formBuilder.group({
      leadSellerDealershipSelection: this.DEFAULT,
      vin: ['', [Validators.pattern(/[a-zA-Z0-9]{17}/), Validators.minLength(17), Validators.maxLength(17)]]
    });

    this.vinPostingForm
      .get('vin')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(vinEntry => (this.targetVin = vinEntry.toUpperCase()));

    this.showSpinner$.pipe(takeUntil(this.destroyed$)).subscribe(_showSpinner => {
      this.showSpinner = _showSpinner;
    });

    this.getTranslations();
    // to clear previously set chrome input data in store
    this.store.dispatch(new SetChromeDataInput(undefined));

    if (this.isLeadSeller) {
      this.loadLeadSellerDealershipOptions();
    }

    this.initializeThirdPartyPostingForm();

    const leadSellerDealershipControl = this.vinPostingForm.get(this.LEAD_SELLER_DEALERSHIP_SELECT);
    leadSellerDealershipControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(oeUuid => {
      this.handleDealershipSelection(oeUuid);
    });

    const leadSellerThirdPartyDealershipSelection = this.thirdPartyPostingForm.get(this.LEAD_SELLER_DEALERSHIP_SELECT);
    leadSellerThirdPartyDealershipSelection?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(oeUuid => {
      this.handleDealershipSelection(oeUuid);
    });
  }

  handleDealershipSelection(oeUuid: string) {
    if (oeUuid === this.DEFAULT) {
      this.selectedDealership = undefined;
    }
    this.setSelectedDealership(oeUuid);
    this.setThirdPartyFormDealershipSelection(oeUuid);
    this.setNewPostLeadSellerDealershipSelection(oeUuid);
    const vin = this.store.selectSnapshot(EditPostingState.deepLinkVin);
    const vendorId = this.store.selectSnapshot(EditPostingState.deepLinkVendor);
    if (vin !== undefined && vendorId !== undefined && this.isLeadSeller) {
      setTimeout(() => {
        this.beginThirdPartyPosting({
          vin: vin,
          vendorId: vendorId
        } as ViwInventoryRecord);
      }, 1000);
    }
  }

  setSelectedDealership(selectedOeUuid: string) {
    this.dealershipOptions.forEach(dealership => {
      if (dealership.oeUuid === selectedOeUuid) {
        dealership.isSelected = true;
        this.selectedDealership = dealership;
      } else {
        dealership.isSelected = false;
      }
    });
  }

  setNewPostLeadSellerDealershipSelection(oeUuid: string) {
    this.vinPostingForm.get(this.LEAD_SELLER_DEALERSHIP_SELECT).patchValue(oeUuid, { emitEvent: false });
  }

  setThirdPartyFormDealershipSelection(oeUuid: string) {
    this.thirdPartyPostingForm.get(this.LEAD_SELLER_DEALERSHIP_SELECT).patchValue(oeUuid, { emitEvent: false });
  }

  loadLeadSellerDealershipOptions() {
    this.leadSellerGroupService
      .getAllLeadSellerDealerGroups()
      .pipe(take(1))
      .subscribe(result => {
        if (result.length > 0) {
          this.formatAndSetDealershipOptions(result);
        } else {
          this.dealershipOptions = [];
          console.log('No results found for lead seller dealership options.');
        }
      });
  }

  formatAndSetDealershipOptions(dealerGroupMemberDto: DealerGroupMemberDto[]) {
    const builtOptions = this.buildDealershipOptions(dealerGroupMemberDto);
    this.dealershipOptions = this.alphabetizeDealershipOptions(builtOptions);
  }

  buildDealershipOptions(dealerDtos: DealerGroupMemberDto[]): DealerGroupMember[] {
    if (dealerDtos?.length) {
      return dealerDtos.map(
        dealerDto =>
          ({
            oeName: dealerDto.organization.oeName,
            oeUuid: dealerDto.organization.oeUuid,
            isSelected: false
          } as DealerGroupMember)
      );
    } else {
      return [];
    }
  }

  alphabetizeDealershipOptions(dealerGroupMembers: DealerGroupMember[]): DealerGroupMember[] {
    if (dealerGroupMembers?.length) {
      return dealerGroupMembers?.sort((a, b) => {
        if (a.oeName.toUpperCase() < b.oeName.toUpperCase()) {
          return -1;
        } else {
          return a.oeName.toUpperCase() > b.oeName.toUpperCase() ? 1 : 0;
        }
      });
    } else {
      return [];
    }
  }

  initializeThirdPartyPostingForm() {
    this.thirdPartyPostingForm = this.formBuilder.group({
      leadSellerDealershipSelection: this.DEFAULT,
      vin: [
        '',
        [
          Validators.pattern('^[a-zA-Z0-9]*$'),
          Validators.pattern(/[a-zA-Z0-9]{6}/),
          Validators.minLength(6),
          Validators.maxLength(17)
        ]
      ]
    });

    this.thirdPartyPostingForm
      .get('vin')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(vinEntry => (this.thirdPartyTargetVin = vinEntry.toUpperCase()));
  }

  /**
   * Method to handle Third Party posting deep linking and posting tab selection via URL query parameters.
   */
  handleQueryParams() {
    const routeSnapshot = this.activatedRoute.snapshot;
    const thirdPartyDeepLinkParamsExist =
      routeSnapshot.queryParamMap.has('hidSystemID') && routeSnapshot.queryParamMap.has('hidVin');
    this.isDeepLink = thirdPartyDeepLinkParamsExist;
    const thirdPartyTabParamSelected =
      routeSnapshot.queryParamMap.has('activeTab') &&
      routeSnapshot.queryParamMap.get('activeTab') === PostingTabs[PostingTabs.THIRD_PARTY_POST];

    if (this.isThirdPartyPostingEnabled && (thirdPartyTabParamSelected || thirdPartyDeepLinkParamsExist)) {
      this.pageLoadAnalytics(PostingFormTitle.NEW_THIRD_PARTY);
      this.selectActiveTab(PostingTabs.THIRD_PARTY_POST);
      if (thirdPartyDeepLinkParamsExist) {
        if (!this.isLeadSeller) {
          setTimeout(() => {
            this.beginThirdPartyPosting({
              vin: routeSnapshot.queryParamMap.get('hidVin'),
              vendorId: routeSnapshot.queryParamMap.get('hidSystemID')
            } as ViwInventoryRecord);
          }, 1000);
        } else {
          //Set vin and vendor name from deeplink for lead seller login and use them to continue third party posting
          //after dealership is selected in dropdown
          const vinFromDeepLink = routeSnapshot.queryParamMap.get('hidVin');
          const vendorIdFromDeepLink = routeSnapshot.queryParamMap.get('hidSystemID');
          this.store.dispatch(new SetDeepLinkVin(vinFromDeepLink));
          this.store.dispatch(new SetDeepLinkVendor(vendorIdFromDeepLink));
        }
      }
    } else {
      this.pageLoadAnalytics(PostingFormTitle.NEW);
      this.selectActiveTab(PostingTabs.POST);
    }
    this.clearQueryParams();
  }

  pageLoadAnalytics(tabName) {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      tabName,
      'Enter VIN'
    );
  }

  /**
   * Method to set the active posting tab based on the presence of the activeTab query parameter.
   */
  selectActiveTab(selectedTab: PostingTabs) {
    setTimeout(() => {
      this.activeTab = selectedTab;
      this.postingTabset.tabs[selectedTab].active = true;
    });
  }

  /**
   * Clear query parameters once they have been handled.
   */
  clearQueryParams() {
    this.router.navigate(['.'], {
      relativeTo: this.activatedRoute,
      queryParams: {}
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    this.handleQueryParams();
    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        filter(e => e instanceof NavigationEnd && e.url.includes('?'))
      )
      .subscribe(
        result => {
          this.handleQueryParams();
        },
        () => {
          this.clearQueryParams();
        }
      );

    if (this.isLeadSeller) {
      this.focusToNewPostDealershipSelect();
    } else {
      this.focusToVinInput();
    }
  }

  getTranslations() {
    const translationKeys = {
      common: {},
      'sellModule.posting.newPosting': {}
    };
    this.translocoService
      .selectTranslateObject(translationKeys)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translations => {
        this.textResults = translations[0].textResults;
        this.DEFAULT_INSPECTION_ACTIONS.forEach(inspectionAction => {
          inspectionAction.value = translations[1].inspectionModalActions[`${inspectionAction.name}`];
          inspectionAction.ariaLabel = translations[1].inspectionModalActions[`${inspectionAction.name}`];
        });
        this.newPostTranslations.textEnterValidVinError = translations[1].textEnterValidVinError;
        this.newPostTranslations.textNewPostTabHeader = translations[1].textNewPostTabHeader;
        this.newPostTranslations.textPostButton = translations[1].textPostButton;
        this.newPostTranslations.textPostingHeader = translations[1].textPostingHeader;
        this.newPostTranslations.textThirdPartyPostTabHeader = translations[1].textThirdPartyPostTabHeader;
        this.newPostTranslations.textVinDecodePlaceholder = translations[1].textVinDecodePlaceholder;
        this.newPostTranslations.dealershipsLabel = translations[1].dealershipsLabel;
        this.newPostTranslations.noDealershipsFoundMessage = translations[1].noDealershipsFoundMessage;
        this.newPostTranslations.select = translations[1].selectOption;
        this.newPostTranslations.selectDealer = translations[1].selectDealerText;

        this.thirdPartyPostTranslations.textThirdPartyPostVinPlaceHolder =
          translations[1].textThirdPartyPostVinPlaceHolder;
        this.thirdPartyPostTranslations.textThirdPartyPostSearchButton = translations[1].textThirdPartyPostSearchButton;
        this.thirdPartyPostTranslations.textEnterValidVinError = translations[1].textEnterValidVinError;
      });
  }

  /**
   * This method submits the user entered VIN to our VIN Decode service (IIS).
   */
  decodeVin() {
    if (this.vinPostingForm.valid && this.targetVin?.length > 0) {
      if (this.showSpinner === false) {
        this.store.dispatch(new ToggleSpinner());
      }
      this.newPostingModal.show();
      this.clearFocus();
      this.inventoryService
        .decodeVin(this.targetVin)
        .pipe(take(1))
        .subscribe(
          response => {
            this.errorHeaderKey = undefined;
            this.errorMessageKey = undefined;
            this.vinDecodeResponse = response;
            this.vinDecodeVehicleInformation = response.vehicleInformation;
            this.chromeDataInput = {
              ...this.chromeDataInput,
              manufacturerModelCode: response?.vehicleInformation?.manufacturerModelCode,
              manufacturerOptionCodes: response?.manufacturerOptionCodes
            };
            this.store.dispatch(new SetChromeDataInput(this.chromeDataInput));
            this.store.dispatch(new ToggleSpinner());
            this.focusModal();
            this.postingAnalyticsHelperService.trackModalView('newPostMakeModel');
          },
          error => {
            this.postingErrorResponse = this.postingDataHelperService.handleErrorMessaging(error);
            this.errorHeaderKey = this.postingErrorResponse.errorHeaderKey;
            this.errorMessageKey = this.postingErrorResponse.errorMessageKey;
            this.store.dispatch(new ToggleSpinner());
            this.focusModal();
            this.postingAnalyticsHelperService.trackCustomError(this.errorMessageKey);
          }
        );
    } else {
      // VIN is not valid, so we will not dispatch unnecessary requests.
      return;
    }
  }

  /**
   * Clear focus on the vin posting input and button elements.
   */
  clearFocus() {
    this.vinFormInputElement.nativeElement.blur();
    this.vinSubmitButtonElement.nativeElement.blur();
    if (this.newPostDealershipSelect) {
      this.newPostDealershipSelect.nativeElement.blur();
    }
    if (this.thirdPartyPostDealershipSelect) {
      this.thirdPartyPostDealershipSelect.nativeElement.blur();
    }
  }

  /**
   * Bring the user focus to the modal once it is open so that keypress events can be captured
   * without tabbing into the modal.
   */
  focusModal() {
    setTimeout(() => {
      this.elRef.nativeElement.querySelector('.modal__title').focus();
    });
  }

  getInspectionReports() {
    let postingErrorResponse;
    this.store.dispatch(new ToggleSpinner());
    const inspectionRequest = {
      getInspectionReport: true,
      getVehicleOptions: false
    } as InspectionOptionRequestDto;
    this.inventoryService
      .getInspectionData(this.targetVin, inspectionRequest)
      .pipe(take(1))
      .subscribe(
        response => {
          this.existingInspectionReport = response?.inspectionReport;
          this.errorMessageKey = undefined;
          this.errorHeaderKey = undefined;
          this.setAvailableInspectionActions();
          this.store.dispatch(new ToggleSpinner());
        },
        error => {
          postingErrorResponse = this.postingDataHelperService.handleErrorMessaging(error);
          this.errorHeaderKey = postingErrorResponse.errorHeaderKey;
          this.errorMessageKey = postingErrorResponse.errorMessageKey;
          this.setAvailableInspectionActions();
          this.store.dispatch(new ToggleSpinner());
        }
      );
  }

  getChromeVehicleData(selectedInspectionOption: NewPostingInspectionOption) {
    this.store.dispatch(new ToggleSpinner());
    let postingErrorResponse;
    const chromeOptionsRequest = this.postingDataHelperService.buildChromeOptionsRequest(
      selectedInspectionOption === NewPostingInspectionOption.USE_EXISTING,
      '',
      [],
      this.existingInspectionReport
    );
    this.inventoryService
      .getInspectionData(this.targetVin, chromeOptionsRequest)
      .pipe(take(1))
      .subscribe(
        response => {
          this.chromeOptions = response?.vehicleOptions;
          this.store.dispatch([
            new SetSelectedInspectionOption(selectedInspectionOption),
            new SetInitialPosting(this.constructPostingStateSeedData(selectedInspectionOption)),
            new ToggleSpinner()
          ]);
          this.navigateToNewPostingScreen(this.targetVin);
        },
        error => {
          postingErrorResponse = this.postingDataHelperService.handleErrorMessaging(error);
          this.errorMessageKey = postingErrorResponse.errorMessageKey;
          this.errorHeaderKey = postingErrorResponse.errorHeaderKey;
          this.store.dispatch(new ToggleSpinner());
        }
      );
  }

  constructPostingStateSeedData(selectedInspectionOption: NewPostingInspectionOption): PostingDataResponseModel {
    const useExistingInspectionReport = selectedInspectionOption === NewPostingInspectionOption.USE_EXISTING;
    return {
      inventoryItem: {
        postingInformation: {},
        disclosures: {},
        inspectionReport: useExistingInspectionReport ? this.existingInspectionReport : {},
        vehicleInformation: {
          ...this.vinDecodeVehicleInformation,
          // Initialize odometer as null so the user is required to enter an updated one on new posting screen.
          odometer: null
        },
        vehicleOptions: this.chromeOptions
      } as PostingInventoryItem,
      sellerOeUuid: this.getSellerUuidByRoleAndDeepLink()
    } as PostingDataResponseModel;
  }

  navigateToNewPostingScreen(vin: string) {
    this.newPostingModal.close();
    this.router.navigate([`/inventory/post/${vin}`]);
  }

  /**
   * Cleanup existing data objects we are keeping in memory on modal close.
   */
  handleModalClose() {
    this.existingInspectionReport = undefined;
    this.errorMessageKey = undefined;
    this.errorHeaderKey = undefined;
    this.vinDecodeVehicleInformation = undefined;
    this.chromeOptions = undefined;
    this.inspectionFetched = false;
    this.translatedInspectionActions = undefined;

    this.focusToVinInput();
  }

  focusToVinInput() {
    // Set timeout is required because we need to wait for the view to reinitialize
    // (so that the element can be focused) after the modal closes.
    setTimeout(() => {
      this.vinFormInputElement.nativeElement.focus();
    });
  }

  focusToNewPostDealershipSelect() {
    // Set timeout is required because we need to wait for the view to reinitialize
    // (so that the element can be focused) after the modal closes.
    setTimeout(() => {
      this.newPostDealershipSelect.nativeElement.focus();
    });
  }

  /**
   * Method to remove the 'Use Existing' option from the actions when no inspection report previously exists.
   */
  setAvailableInspectionActions() {
    this.inspectionFetched = true;
    this.postingAnalyticsHelperService.trackModalView('newPostInspection');
    const translatedDefaultActions = cloneDeep(this.DEFAULT_INSPECTION_ACTIONS);
    if (this.existingInspectionReport?.inspectionData) {
      this.translatedInspectionActions = translatedDefaultActions;
    } else {
      const filteredInspectionActions = translatedDefaultActions.filter(action => action.name !== 'USE_EXISTING');
      filteredInspectionActions[0].isChecked = true;
      this.translatedInspectionActions = filteredInspectionActions;
    }
  }

  get isThirdPartyPostingEnabled() {
    return this._launchDarklyService.getFeatureFlag('sell-thirdPartyPostingEnabled', false);
  }

  get showThirdPartyResultSection() {
    return this.activeTab === PostingTabs.THIRD_PARTY_POST && (this.showSpinner || this.thirdPartySearchResults);
  }

  findThirdPartyData(vin: string) {
    this.thirdPartySearchResults = null;
    this.targetVin = vin;
    if (!this.showSpinner) {
      this.store.dispatch(new ToggleSpinner());
      this.editPostingService
        .getThirdPartyInventoryRecords(vin)
        .pipe(take(1))
        .subscribe(
          response => {
            this.thirdPartySearchResults = response;
            this.store.dispatch(new ToggleSpinner());
          },
          error => {
            this.postingErrorResponse = this.postingDataHelperService.handleErrorMessaging(error);
            this.errorHeaderKey = this.postingErrorResponse.errorHeaderKey;
            this.errorMessageKey = this.postingErrorResponse.errorMessageKey;
            this.newPostingModal.show();
            this.postingAnalyticsHelperService.trackCustomError(this.errorMessageKey);
            this.store.dispatch(new ToggleSpinner());
          }
        );
    }
  }

  /**
   * Initiate posting a vehicle from a third party data provider.
   * @param viwSearchResult the VIW inventory record to be posted.
   */
  beginThirdPartyPosting(viwSearchResult: ViwInventoryRecord) {
    this.targetVin = viwSearchResult.vin;
    this.store.dispatch(new ToggleSpinner());
    this.newPostingModal.show();

    let viwRecordWithFormattedImages;

    // forkJoin the call to VIN Decode Asset service and VIW Inventory endpoint to make requests in parallel.
    forkJoin([
      this.inventoryService.decodeVin(viwSearchResult.vin),
      this.editPostingService.getThirdPartyInventoryRecord(viwSearchResult.vendorId, viwSearchResult.vin)
    ])
      .pipe(
        // With the results of VIN Decode and VIW calls, set fields and call Chrome with the data.
        mergeMap(([vinDecodeResponse, viwInventoryRecord]) => {
          this.vinDecodeResponse = vinDecodeResponse;
          this.vinDecodeVehicleInformation = vinDecodeResponse.vehicleInformation;
          // The getThirdPartyInventoryRecord endpoint converts image urls into secure and usable format.
          viwRecordWithFormattedImages = viwInventoryRecord;
          // Request Chrome options to populate available and selected options in the posting screens.
          this.chromeDataInput.manufacturerModelCode = this.vinDecodeVehicleInformation?.manufacturerModelCode;
          this.chromeDataInput.manufacturerOptionCodes = this.vinDecodeResponse?.manufacturerOptionCodes;
          this.chromeDataInput.equipmentDescriptions = this.postingDataHelperService.formatAccessoriesForChrome(
            viwInventoryRecord?.accessories
          );
          this.store.dispatch(new SetChromeDataInput(this.chromeDataInput));
          return this.inventoryService.getInspectionData(
            this.targetVin,
            this.postingDataHelperService.buildThirdPartyChromeOptionsRequest(
              viwRecordWithFormattedImages,
              vinDecodeResponse
            )
          );
        })
      )
      .subscribe(
        chromeResponse => {
          this.chromeOptions = chromeResponse.vehicleOptions;
          this.store.dispatch([
            new SetSelectedInspectionOption(NewPostingInspectionOption.CREATE_NEW),
            new SetInitialPosting(this.mapThirdPartyDataToPostingResponse(viwRecordWithFormattedImages)),
            new ToggleSpinner()
          ]);
          this.navigateToNewPostingScreen(this.targetVin);
        },
        // Handle errors for any of the 3 previous calls.
        error => {
          this.postingErrorResponse = this.postingDataHelperService.handleErrorMessaging(error);
          this.errorHeaderKey = this.postingErrorResponse.errorHeaderKey;
          this.errorMessageKey = this.postingErrorResponse.errorMessageKey;
          this.store.dispatch(new ToggleSpinner());
          this.focusModal();
          this.postingAnalyticsHelperService.trackCustomError(this.errorMessageKey);
        }
      );
  }

  /**
   * Map all of the attributes from the VIW Inventory record, VIN Decode, and Chrome service responses into
   * the PostingDataResponseModel we will use on the posting screens.
   * @param inventoryRecord
   */
  mapThirdPartyDataToPostingResponse(inventoryRecord: ViwInventoryRecord): PostingDataResponseModel {
    return {
      sellerOeUuid: this.getSellerUuidByRoleAndDeepLink(),
      inventoryItem: {
        postingInformation: {
          stockNumber: inventoryRecord.dealerStockNumber,
          thirdPartyPosting: true,
          externalSystemCode: inventoryRecord.vendorId
        } as PostingInformation,
        disclosures: {},
        inspectionReport: {
          itemId: null,
          reportUrl: null,
          inspectionData: {
            inspectionCompany: inventoryRecord.inspectionCompanyId,
            statusDate: inventoryRecord.inspectionDate?.toString(),
            viwSource: null,
            vinGuid: null,
            inspectionCompanyId: inventoryRecord.inspectionCompanyId,
            conditionGrade: inventoryRecord.conditionGrade ? Number.parseInt(inventoryRecord.conditionGrade) : null,
            dealerCity: inventoryRecord.dealerCity,
            account: inventoryRecord.account,
            dateReturned: inventoryRecord.dateReturned?.toString(),
            dealerName: inventoryRecord.dealerName,
            dealerNumber: inventoryRecord.dealerNumber,
            dealerAddress: inventoryRecord.dealerAddress,
            dealerAddress2: inventoryRecord.dealerAddress2,
            dealerState: inventoryRecord.dealerState,
            dealerZip: inventoryRecord.dealerZip,
            dealerPhone: inventoryRecord.dealerPhone,
            inspectorId: inventoryRecord.inspectorId,
            inspectionRequestDate: inventoryRecord.inspectionRequestDate?.toString(),
            inspectionDate: inventoryRecord.inspectionDate?.toString(),
            comments: inventoryRecord.comments,
            chargeableTotal: inventoryRecord.chargeableTotal,
            nonChargeableTotal: inventoryRecord.nonChargeableTotal,
            total: inventoryRecord.total,
            vehicle: {
              vin: inventoryRecord.vin,
              year: this.vinDecodeVehicleInformation?.modelYear,
              make: this.vinDecodeVehicleInformation?.make?.makeDescription,
              model: this.vinDecodeVehicleInformation?.model?.modelDescription,
              series: this.vinDecodeVehicleInformation?.series?.seriesDescription,
              exteriorColor: inventoryRecord.exteriorColor,
              interiorColor: inventoryRecord.interiorColor,
              mileage: inventoryRecord.mileage,
              interiorMaterial: inventoryRecord.interiorMaterial,
              licensePlate: inventoryRecord.licensePlate,
              licenseState: inventoryRecord.plateState,
              accessories: null,
              tires: inventoryRecord.tires,
              smokerFlag: inventoryRecord.smokerFlag
            } as InspectionVehicleDto,
            images: inventoryRecord.images,
            extendedAttributes: {
              accountType: inventoryRecord.accountType,
              owner: inventoryRecord.owner,
              dataProvider: inventoryRecord.vendorId,
              newPosting: inventoryRecord.newPosting,
              postingPrice: inventoryRecord.postingPrice,
              titleState: inventoryRecord.titleState,
              subOwner: inventoryRecord.subowner
            } as InspectionExtendedAttributesDto,
            inspectionFlags: {},
            exceptions: inventoryRecord.exceptions,
            previousRepairAmount: null,
            previousRepairDesc: null
          } as InspectionDataDto
        } as InspectionReportDto,
        vehicleInformation: {
          vin: inventoryRecord.vin,
          manufacturerModelCode: this.vinDecodeVehicleInformation?.manufacturerModelCode,
          vehicleDescription: this.vinDecodeVehicleInformation?.vehicleDescription,
          manufacturer: {
            vehicleManufacturerCode: this.vinDecodeVehicleInformation?.manufacturer?.vehicleManufacturerCode,
            vehicleManufacturerName: this.vinDecodeVehicleInformation?.manufacturer?.vehicleManufacturerName
          },
          modelYear: this.vinDecodeVehicleInformation?.modelYear,
          trim: this.vinDecodeVehicleInformation?.trim,
          commercialVehicle: this.vinDecodeVehicleInformation?.commercialVehicle,
          driveTrain: this.vinDecodeVehicleInformation?.driveTrain,
          fuelType: this.vinDecodeVehicleInformation?.fuelType,
          engineType: this.vinDecodeVehicleInformation?.engineType,
          make: this.vinDecodeVehicleInformation?.make,
          model: this.vinDecodeVehicleInformation?.model,
          recreationalVehicle: this.vinDecodeVehicleInformation?.recreationalVehicle,
          series: this.vinDecodeVehicleInformation?.series,
          transmissionType: this.vinDecodeVehicleInformation?.transmissionType,
          vehicleClass: this.vinDecodeVehicleInformation?.vehicleClass,
          vehicleFamily: this.vinDecodeVehicleInformation?.vehicleFamily,
          // Initialize odometer so it is populated on the new posting screen.
          odometer: inventoryRecord.mileage
        } as VehicleInformation,
        vehicleOptions: this.chromeOptions,
        titleState: {
          stateName: inventoryRecord.titleState,
          stateCode: null
        }
      } as PostingInventoryItem
    } as PostingDataResponseModel;
  }

  getSellerUuidByRoleAndDeepLink() {
    const uuid = this.isLeadSeller ? this.selectedDealership?.oeUuid || this.sellerOeUuid : this.sellerOeUuid;
    console.log('[Posting] getSellerUuidByRoleAndDeepLink - isLeadSeller:', this.isLeadSeller,
      '| selectedDealership.oeUuid:', this.selectedDealership?.oeUuid,
      '| sellerOeUuid:', this.sellerOeUuid, '| resolved:', uuid);
    return uuid;
  }

  get isLeadSeller() {
    return this.store.selectSnapshot(ConfigurationState.hasRole('Lead_Seller'));
  }

  isLeadSellerDealerChosen(formGroup: UntypedFormGroup) {
    const selectedValue = formGroup.get(this.LEAD_SELLER_DEALERSHIP_SELECT).value;
    return this.isLeadSeller && selectedValue !== this.DEFAULT && selectedValue !== undefined && selectedValue !== null;
  }

  get isDealershipOptionsEmpty() {
    return !!this.dealershipOptions && this.dealershipOptions.length < 1;
  }
}
